'use client';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';

import { Button } from '@/components/ui/button';

import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';

import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { CldImage } from 'next-cloudinary';
import { useEffect, useState } from 'react';

export default function PropertyGallery({
  startIndex = 0,
  images,
  setIsGalleryOpen,
  isGalleryOpen,
}: {
  startIndex: number;
  images: string[];
  isGalleryOpen: boolean;
  setIsGalleryOpen: (_open: boolean) => void;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(startIndex);
  const [viewMode, setViewMode] = useState<'grid' | 'slider'>('grid');

  // Set up keyboard navigation
  useEffect(() => {
    if (!isGalleryOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          if (viewMode === 'slider') api?.scrollPrev();
          break;
        case 'ArrowRight':
          if (viewMode === 'slider') api?.scrollNext();
          break;
        case 'Escape':
          if (viewMode === 'slider') {
            setViewMode('grid');
          } else {
            setIsGalleryOpen(false);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [api, isGalleryOpen, setIsGalleryOpen, viewMode]);

  // Update current index when API or isGalleryOpen changes
  useEffect(() => {
    if (!api) return;

    if (isGalleryOpen && viewMode === 'slider') {
      // Set initial slide when dialog opens
      api.scrollTo(current);

      // Update current index when carousel changes
      const handleSelect = () => {
        setCurrent(api.selectedScrollSnap());
      };

      api.on('select', handleSelect);
      return () => {
        api.off('select', handleSelect);
      };
    }
  }, [api, isGalleryOpen, current, viewMode]);

  // Reset to grid view when dialog closes
  useEffect(() => {
    if (!isGalleryOpen) {
      setViewMode('grid');
    }
  }, [isGalleryOpen]);

  // Update current when startIndex changes (from parent component)
  useEffect(() => {
    setCurrent(startIndex);
  }, [startIndex]);

  // Switch to full-screen slider with specific image
  const openSlider = (index: number) => {
    setCurrent(index);
    setViewMode('slider');
  };

  return (
    <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
      <DialogContent className="max-w-full max-h-screen p-0 h-screen w-screen bg-white">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4 flex justify-between">
          {viewMode === 'slider' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('grid')}
              className="gap-2 bg-white hover:bg-white/90 rounded-full p-2 h-auto w-auto"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back to gallery</span>
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsGalleryOpen(false)}
              className="gap-2 bg-white hover:bg-white/90 rounded-full p-2 h-auto w-auto"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </Button>
          )}

          <DialogClose asChild>
            <Button
              variant="ghost"
              size="sm"
              className="bg-white hover:bg-white/90 rounded-full p-2 h-auto w-auto"
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogClose>
        </div>

        {/* Main Content Area */}
        <div className="w-full h-full pt-14">
          {' '}
          {/* Reduced padding to give more room */}
          {viewMode === 'grid' ? (
            /* Masonry Grid View */
            <div className="h-[calc(100vh-6rem)] overflow-y-auto pb-8 px-4">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pb-6">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="relative overflow-hidden rounded-md cursor-pointer aspect-square"
                      onClick={() => openSlider(index)}
                    >
                      <div className="group relative h-full w-full">
                        <CldImage
                          className="object-cover w-full h-full transition-transform group-hover:scale-105 duration-300"
                          width="400"
                          height="400"
                          alt={`Property image ${index + 1}`}
                          src={image}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-lg">
                            View
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Simple, reliable slider view */
            <div className="flex flex-col h-full w-screen">
              {/* Main image view - explicitly sized for reliability */}
              <div className="flex-grow flex flex-col">
                {/* Image viewer */}
                <div className="flex-grow flex items-center justify-center">
                  <Carousel
                    opts={{
                      loop: true,
                      startIndex: current,
                    }}
                    setApi={setApi}
                    className="w-full"
                  >
                    <CarouselContent>
                      {images.map((image, index) => (
                        <CarouselItem key={index} className="flex justify-center items-center">
                          {/* Simple wrapper with fixed padding */}
                          <div className="flex justify-center items-center">
                            {/* Image with explicit width constraint but maintains aspect ratio */}
                            <div className="max-w-screen-xl w-full flex justify-center">
                              <CldImage
                                src={image}
                                width={800}
                                height={600}
                                alt={`Property photo ${index + 1}`}
                                className="object-contain max-h-[calc(100vh-170px)] w-screen mx-auto"
                              />
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>

                    {/* Navigation buttons positioned absolutely */}
                    <Button
                      variant="ghost"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full shadow-md z-10"
                      onClick={() => api?.scrollPrev()}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>

                    <Button
                      variant="ghost"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full shadow-md z-10"
                      onClick={() => api?.scrollNext()}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </Carousel>
                </div>

                {/* Counter */}
                <div className="text-center py-2 font-medium text-sm text-gray-600">
                  {current + 1} / {images.length}
                </div>
              </div>

              {/* Thumbnail strip - fixed height for reliability */}
              <div className="h-24 border-t bg-white w-screen overflow-x-scroll">
                <div
                  className="flex gap-2 px-4 h-full items-center"
                  style={{ minWidth: 'max-content' }}
                >
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => api?.scrollTo(index)}
                      className={`flex-shrink-0 h-16 w-16 rounded-md overflow-hidden ${
                        current === index ? 'ring-2 ring-blue-500' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <CldImage
                        src={image}
                        width={80}
                        height={80}
                        alt={`Thumbnail ${index + 1}`}
                        className="object-cover h-full w-full"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
